import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { ApprovalBannerProps, ApprovalStep } from './MasterDocument.types';

// ─── Step Circle ───────────────────────────────────────────

function StepCircle({ step, index }: { step: ApprovalStep; index: number }) {
  const baseCircle = 'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0';

  switch (step.status) {
    case 'completed':
      return (
        <div className={clsx(baseCircle, 'bg-green-700 dark:bg-green-600 text-white dark:text-dark-content-inverse')}>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'current':
      return (
        <div className={clsx(baseCircle, 'border-2 border-amber-700 dark:border-amber-500 text-amber-700 dark:text-amber-400 bg-transparent')}>
          {index + 1}
        </div>
      );
    case 'pending':
      return (
        <div className={clsx(baseCircle, 'bg-background-secondary dark:bg-dark-bg-secondary text-content-secondary dark:text-dark-content-secondary')}>
          {index + 1}
        </div>
      );
  }
}

// ─── Connector Line ────────────────────────────────────────

function ConnectorLine({ status }: { status: ApprovalStep['status'] }) {
  return (
    <div
      className={clsx(
        'flex-1 h-0.5 mx-1',
        status === 'completed' ? 'bg-green-700 dark:bg-green-600' : 'bg-background-secondary dark:bg-dark-bg-secondary',
      )}
    />
  );
}

// ─── ApprovalBanner ────────────────────────────────────────

export function ApprovalBanner({
  steps,
  message,
  onApprove,
  approveLabel,
  onReject,
  rejectLabel,
  className,
}: ApprovalBannerProps) {
  const { t } = useMasterDocumentTranslation();
  return (
    <div
      className={clsx(
        'rounded-xl px-[18px] py-3 bg-amber-50 animate-[fadeIn_200ms_ease-out]',
        'dark:bg-amber-700/15',
        className,
      )}
      role="alert"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Steps visual */}
        <div className="flex items-center gap-0 flex-1 min-w-0">
          {steps.map((step, i) => (
            <div key={step.label} className="contents">
              {i > 0 && <ConnectorLine status={steps[i - 1].status} />}
              <div className="flex flex-col items-center gap-0.5 shrink-0">
                <StepCircle step={step} index={i} />
                <span
                  className={clsx(
                    'text-xs font-semibold whitespace-nowrap max-w-[60px] truncate text-center',
                    step.status === 'current' ? 'text-amber-700 dark:text-amber-400' : 'text-content-secondary dark:text-dark-content-secondary',
                    step.status === 'completed' && 'text-green-700 dark:text-green-400',
                  )}
                >
                  {step.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Message */}
        <p className="text-sm text-amber-700 dark:text-amber-400 flex-1 min-w-0">
          {message}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onReject}
            className={clsx(
              'px-3.5 py-1.5 rounded-md text-xs font-semibold',
              'border border-amber-700/30 text-amber-700 hover:bg-amber-700/10 dark:hover:bg-amber-700/20 transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'dark:text-amber-400 dark:border-amber-400/30',
            )}
          >
            {rejectLabel ?? t('master_document.approval.reject')}
          </button>
          <button
            type="button"
            onClick={onApprove}
            className={clsx(
              'px-4 py-1.5 rounded-md text-xs font-semibold',
              'bg-primary-inverse-background text-white hover:bg-primary-700 transition-all duration-150',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'min-h-[32px]',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            {approveLabel ?? t('master_document.approval.approve')}
          </button>
        </div>
      </div>
    </div>
  );
}
